import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  MenuItem,
  Typography,
} from '@material-ui/core'
import React from 'react'
import { ChevronDown } from 'react-feather'
import { Control, FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  areaUnit,
  hourUnit,
  lengthUnit,
  powerPer1000Unit,
  powerPerLengthUnit,
  powerUnit,
} from '../../../../util/formatters'
import { validators } from '../../../../validation/validators'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { FormGroup } from '../../../form/FormGroup'
import { FormValues } from '../form-values'
import { theme } from '../../../../theme'
import { ScheduleFragment } from '../../../../generated/graphql'

type Props = {
  schedules: ScheduleFragment[]
  control: Control<FormValues>
  errors: FieldErrors<FormValues>
}

export function DomesticHotWaterForm({
  schedules,
  control,
  errors,
}: Props): React.ReactElement {
  const { t } = useTranslation()

  return (
    <FormGroup width="100%">
      <Accordion elevation={3}>
        <AccordionSummary
          expandIcon={<ChevronDown id="collection-schedule-expand-button" />}
        >
          <Box display="flex" alignItems="center">
            <Typography>
              {t('energySystem.parameters.dhw.tabs.general')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.domesticHotWater.consumptionDensity"
                error={errors.parameters?.domesticHotWater?.consumptionDensity}
                label={t('energySystem.parameters.dhw.consumption')}
                rules={{
                  ...validators.required,
                  ...validators.copModifier,
                }}
                InputProps={{
                  endAdornment: (
                    <Typography>
                      {powerUnit}
                      {hourUnit}/{areaUnit}
                    </Typography>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.domesticHotWater.distributionEfficiency"
                error={
                  errors.parameters?.domesticHotWater?.distributionEfficiency
                }
                label={t('energySystem.parameters.dhw.distributionEfficiency')}
                rules={{
                  ...validators.required,
                  ...validators.efficiency,
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <ControlledTextField
                control={control}
                name="parameters.domesticHotWater.consumptionFactor"
                error={errors.parameters?.domesticHotWater?.consumptionFactor}
                label={t('energySystem.parameters.dhw.consumptionFactor')}
                rules={{
                  ...validators.required,
                  ...validators.copModifier,
                }}
              />
            </Grid>
            <Grid item xs={8}>
              <ControlledTextField
                control={control}
                name="scheduleId"
                error={errors.scheduleId}
                label={t('energySystem.parameters.dhw.consumptionProfile')}
                rules={validators.required}
                select
              >
                {Object.values(schedules).map((schedule) => (
                  <MenuItem key={schedule.id} value={schedule.id}>
                    {schedule.name}
                  </MenuItem>
                ))}
              </ControlledTextField>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion style={{ marginTop: theme.spacing(1) }} elevation={3}>
        <AccordionSummary
          expandIcon={<ChevronDown id="collection-schedule-expand-button" />}
        >
          <Box display="flex" alignItems="center">
            <Typography>
              {t('energySystem.parameters.dhw.tabs.hotWaterCirculation')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.domesticHotWater.specificLength"
                error={errors.parameters?.domesticHotWater?.specificLength}
                label={t('energySystem.parameters.dhw.hwcSpecificLenght')}
                rules={{
                  ...validators.required,
                  ...validators.copModifier,
                }}
                InputProps={{
                  endAdornment: (
                    <Typography>
                      {lengthUnit}/{areaUnit}
                    </Typography>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.domesticHotWater.specificHeatLoss"
                error={errors.parameters?.domesticHotWater?.specificHeatLoss}
                label={t('energySystem.parameters.dhw.hwcSpecificHeatLoss')}
                rules={{
                  ...validators.required,
                  ...validators.copModifier,
                }}
                InputProps={{
                  endAdornment: <Typography>{powerPerLengthUnit}</Typography>,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.domesticHotWater.heatersAmount"
                error={errors.parameters?.domesticHotWater?.heatersAmount}
                label={t('energySystem.parameters.dhw.hotWaterHeaterAmount')}
                rules={{
                  ...validators.required,
                  ...validators.nonNegativeInteger,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.domesticHotWater.heaterPower"
                error={errors.parameters?.domesticHotWater?.heaterPower}
                label={t('energySystem.parameters.dhw.hotWaterHeaterPower')}
                rules={{
                  ...validators.required,
                  ...validators.copModifier,
                }}
                InputProps={{
                  endAdornment: (
                    <Typography>
                      {powerPer1000Unit}/{t('common.amountUnit')}
                    </Typography>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion style={{ marginTop: theme.spacing(1) }} elevation={3}>
        <AccordionSummary
          expandIcon={<ChevronDown id="collection-schedule-expand-button" />}
        >
          <Box display="flex" alignItems="center">
            <Typography>
              {t('energySystem.parameters.dhw.tabs.wasteWater')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.domesticHotWater.wasteWaterHeatContentRatio"
                error={
                  errors.parameters?.domesticHotWater
                    ?.wasteWaterHeatContentRatio
                }
                label={t('energySystem.parameters.dhw.wwHeatContentRatio')}
                rules={{
                  ...validators.required,
                  ...validators.copModifier,
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </FormGroup>
  )
}
